import React, { useState, useEffect, useLayoutEffect, useContext, useRef } from "react";
import { useParams } from "react-router-dom";
// emoji-mart 5 não traz mais CSS próprio nem exporta React: o componente vem
// do pacote @emoji-mart/react e os emojis do @emoji-mart/data.
import Picker from "@emoji-mart/react";
import emojiData from "@emoji-mart/data";
import {
  Paperclip,
  Smile,
  Send,
  X,
  Mic,
  MoreVertical,
  XCircle,
  CheckCircle2,
  Loader2,
  Images,
} from "lucide-react";

import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { cn } from "../../lib/utils";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import RecordingTimer from "./RecordingTimer";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { useThemeContext } from "../../context/DarkMode";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import MediaLibraryPicker from "../MediaLibraryPicker";
import AssistenteDeTexto from "./AssistenteDeTexto";
import { paraWav } from "./wav";

// Gravação de voz com o MediaRecorder do próprio navegador.
//
// Antes era a mic-recorder-to-mp3, que não funciona mais aqui: ela espera o
// codificador `lamejs` como variável global (formato UMD) e o bundler atual
// carrega tudo como módulo ES. O resultado era `ReferenceError: Lame is not
// defined` na primeira tentativa — o gravador nunca chegava a existir, e o
// botão de áudio simplesmente não fazia nada.
//
// O MediaRecorder é padrão do navegador, não tem dependência e grava em
// Opus — que é justamente o codec que o WhatsApp usa para mensagem de voz.
// O MP3 anterior era convertido de qualquer forma antes de sair.

// Formatos por ordem de preferência; o navegador escolhe o primeiro que
// souber gravar. Safari só tem mp4/aac, Chrome e Firefox preferem opus.
const FORMATOS = [
  { mime: "audio/ogg;codecs=opus", ext: "ogg" },
  { mime: "audio/webm;codecs=opus", ext: "webm" },
  { mime: "audio/mp4", ext: "m4a" },
  { mime: "audio/webm", ext: "webm" }
];

const escolherFormato = () => {
  if (typeof MediaRecorder === "undefined") return null;

  return (
    FORMATOS.find((f) => MediaRecorder.isTypeSupported(f.mime)) || {
      mime: "",
      ext: "webm"
    }
  );
};

const MessageInput = ({ ticketStatus }) => {
  const { ticketId } = useParams();

  const [medias, setMedias] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [quickAnswers, setQuickAnswer] = useState([]);
  const [typeBar, setTypeBar] = useState(false);
  const [previewMedia, setPreviewMedia] = useState("");
  const inputRef = useRef();
  const captionRef = useRef();
  // Gravação em curso: o MediaRecorder, o stream do microfone e os pedaços.
  const gravadorRef = useRef(null);
  const pedacosRef = useRef([]);
  const emojiBoxRef = useRef();
  const { setReplyingMessage, replyingMessage } =
    useContext(ReplyMessageContext);
  const { user } = useContext(AuthContext);
  const { darkMode } = useThemeContext();

  const disabled = loading || recording || ticketStatus !== "open";

  // Em tela estreita o texto longo do placeholder é cortado no meio.
  const [placeholderCurto, setPlaceholderCurto] = useState(
    typeof window !== "undefined" && window.innerWidth < 640
  );

  // Sair da tela no meio de uma gravação deixava o microfone aberto para
  // sempre — a luz do aparelho acesa e o aviso de "gravando" na aba.
  useEffect(() => {
    return () => {
      const atual = gravadorRef.current;
      if (!atual) return;
      try {
        if (atual.recorder.state !== "inactive") atual.recorder.stop();
      } catch {
        // Recorder já encerrado por outro caminho.
      }
      atual.stream.getTracks().forEach((trilha) => trilha.stop());
      gravadorRef.current = null;
    };
  }, []);

  useEffect(() => {
    const aoRedimensionar = () => setPlaceholderCurto(window.innerWidth < 640);
    window.addEventListener("resize", aoRedimensionar);
    return () => window.removeEventListener("resize", aoRedimensionar);
  }, []);

  const [signMessage, setSignMessage] = useLocalStorage("signOption", true);

  useEffect(() => {
    inputRef.current.focus();
  }, [replyingMessage]);

  useEffect(() => {
    inputRef.current.focus();
    return () => {
      setInputMessage("");
      setShowEmoji(false);
      setMedias([]);
      setReplyingMessage(null);
    };
  }, [ticketId, setReplyingMessage]);

  useEffect(() => {
    if (!showEmoji) return;
    const handleClickAway = (e) => {
      if (emojiBoxRef.current && !emojiBoxRef.current.contains(e.target)) {
        setShowEmoji(false);
      }
    };
    document.addEventListener("mousedown", handleClickAway);
    return () => document.removeEventListener("mousedown", handleClickAway);
  }, [showEmoji]);

  // Ajusta a altura da caixa de texto dinamicamente conforme o texto cresce ou pula linhas
  const adjustInputHeight = () => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.overflowY = "hidden";
    const scrollHeight = el.scrollHeight;
    if (scrollHeight > 0) {
      const newHeight = Math.max(42, Math.min(scrollHeight, 180));
      el.style.height = `${newHeight}px`;
      el.style.overflowY = scrollHeight > 180 ? "auto" : "hidden";
    }
  };

  const adjustCaptionHeight = () => {
    const el = captionRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.overflowY = "hidden";
    const scrollHeight = el.scrollHeight;
    if (scrollHeight > 0) {
      const newHeight = Math.max(38, Math.min(scrollHeight, 140));
      el.style.height = `${newHeight}px`;
      el.style.overflowY = scrollHeight > 140 ? "auto" : "hidden";
    }
  };

  useLayoutEffect(() => {
    adjustInputHeight();
  }, [inputMessage]);

  useLayoutEffect(() => {
    adjustCaptionHeight();
  }, [inputMessage, medias]);

  useEffect(() => {
    window.addEventListener("resize", adjustInputHeight);
    return () => window.removeEventListener("resize", adjustInputHeight);
  }, []);

  // Miniatura da mídia anexada, só para imagens.
  useEffect(() => {
    const arquivo = medias[0];
    if (!arquivo || !arquivo.type?.startsWith("image/")) {
      setPreviewMedia("");
      return undefined;
    }

    const url = window.URL.createObjectURL(arquivo);
    setPreviewMedia(url);

    return () => window.URL.revokeObjectURL(url);
  }, [medias]);

  // O copiloto (no cabeçalho do ticket) manda o texto por evento, para não
  // precisar levantar o estado da caixa de mensagem até lá.
  useEffect(() => {
    const aoReceber = (evento) => {
      setInputMessage(evento.detail || "");
      inputRef.current?.focus();
    };

    document.addEventListener("copilot:usar-texto", aoReceber);
    return () => document.removeEventListener("copilot:usar-texto", aoReceber);
  }, []);

  const handleChangeInput = (e) => {
    setInputMessage(e.target.value);
    handleLoadQuickAnswer(e.target.value);
  };

  const handleQuickAnswersClick = async (resposta) => {
    setTypeBar(false);
    setInputMessage(resposta.message || "");

    if (!resposta.mediaUrl) return;

    // Traz o arquivo salvo e o põe como anexo pendente, igual a quem escolhe
    // um arquivo do computador. Nada sai sozinho: a pessoa ainda revisa e
    // clica em enviar, que é a promessa da tela de respostas prontas.
    try {
      setLoading(true);
      const resposta_http = await fetch(resposta.mediaUrl);

      if (!resposta_http.ok) throw new Error("Arquivo não encontrado");

      const blob = await resposta_http.blob();
      const nome =
        resposta.mediaName || resposta.mediaUrl.split("/").pop() || "arquivo";

      setMedias([new File([blob], nome, { type: blob.type })]);
    } catch (err) {
      toast.error(
        "Não consegui carregar o arquivo desta resposta. O texto foi preenchido."
      );
    }
    setLoading(false);
  };

  const handleAddEmoji = (e) => {
    let emoji = e.native;
    setInputMessage((prevState) => prevState + emoji);
  };

  const handleChangeMedias = (e) => {
    if (!e.target.files) {
      return;
    }

    const selectedMedias = Array.from(e.target.files);
    setMedias(selectedMedias);
  };

  const handleInputPaste = (e) => {
    if (e.clipboardData.files[0]) {
      setMedias([e.clipboardData.files[0]]);
    }
  };

  const handleUploadMedia = async (e) => {
    setLoading(true);
    e.preventDefault();

    const legenda = inputMessage.trim();

    const formData = new FormData();
    formData.append("fromMe", true);
    medias.forEach((media) => formData.append("medias", media));
    // `body` é a legenda opcional — vai uma só, para o conjunto de arquivos.
    if (legenda) {
      formData.append(
        "body",
        signMessage ? `*${user?.name}:*\n${legenda}` : legenda
      );
    }

    try {
      await api.post(`/messages/${ticketId}`, formData);
      setMedias([]);
      setInputMessage("");
    } catch (err) {
      toastError(err);
    }

    setLoading(false);
  };

  const handleSendMessage = async () => {
    if (inputMessage.trim() === "" || loading) return;
    setLoading(true);

    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      body: signMessage
        ? `*${user?.name}:*\n${inputMessage.trim()}`
        : inputMessage.trim(),
      quotedMsg: replyingMessage,
    };
    try {
      await api.post(`/messages/${ticketId}`, message);
    } catch (err) {
      toastError(err);
    }

    setInputMessage("");
    setShowEmoji(false);
    setLoading(false);
    setReplyingMessage(null);
    inputRef.current?.focus();
  };

  const handleLoadQuickAnswer = async (value) => {
    if (value && value.indexOf("/") === 0) {
      try {
        const { data } = await api.get("/quickAnswers/", {
          // `value` é o texto recém-digitado; `inputMessage` ainda tem o valor
          // anterior neste ponto do render e faria a busca ficar um passo atrás.
          params: { searchParam: value.substring(1) },
        });
        setQuickAnswer(data.quickAnswers);
        setTypeBar(data.quickAnswers.length > 0);
      } catch (err) {
        setTypeBar(false);
      }
    } else {
      setTypeBar(false);
    }
  };

  const handleStartRecording = async () => {
    setLoading(true);
    try {
      // Sem HTTPS o navegador nem expõe o microfone. Acontece direto em
      // produção servida por HTTP, e o erro que aparecia era um
      // "undefined is not a function" que não dizia nada.
      if (!navigator.mediaDevices?.getUserMedia) {
        toast.error(
          "O navegador só libera o microfone em conexão segura (https). " +
            "Peça para quem cuida do servidor habilitar o certificado."
        );
        setLoading(false);
        return;
      }

      const formato = escolherFormato();

      if (!formato) {
        toast.error("Este navegador não sabe gravar áudio.");
        setLoading(false);
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const recorder = new MediaRecorder(
        stream,
        formato.mime ? { mimeType: formato.mime } : undefined
      );

      pedacosRef.current = [];
      // Guarda o stream junto: parar o recorder não desliga o microfone, e
      // sem fechar as trilhas a luz do aparelho fica acesa depois da gravação.
      gravadorRef.current = { recorder, stream, ext: formato.ext };

      recorder.ondataavailable = (evento) => {
        if (evento.data && evento.data.size > 0) {
          pedacosRef.current.push(evento.data);
        }
      };

      // Um pedaço por segundo: sem o intervalo, o navegador só entrega tudo
      // no fim, e uma aba fechada no meio levaria a gravação inteira.
      recorder.start(1000);

      setRecording(true);
      setLoading(false);
    } catch (err) {
      // Permissão negada é o caso mais comum e não é defeito do sistema:
      // merece instrução, não a mensagem técnica do navegador.
      if (err?.name === "NotAllowedError") {
        toast.error(
          "Você bloqueou o microfone para este site. Libere no cadeado ao " +
            "lado do endereço e tente de novo."
        );
      } else if (err?.name === "NotFoundError") {
        toast.error("Nenhum microfone encontrado neste computador.");
      } else {
        toastError(err);
      }
      setLoading(false);
    }
  };

  // Encerra a gravação e devolve o áudio. Fecha o microfone em qualquer
  // caso — inclusive quando a pessoa desiste.
  const encerrarGravacao = () =>
    new Promise((resolve) => {
      const atual = gravadorRef.current;

      if (!atual) {
        resolve(null);
        return;
      }

      const finalizar = () => {
        atual.stream.getTracks().forEach((trilha) => trilha.stop());
        gravadorRef.current = null;

        const blob = new Blob(pedacosRef.current, {
          type: atual.recorder.mimeType || "audio/webm"
        });
        pedacosRef.current = [];

        resolve({ blob, ext: atual.ext });
      };

      if (atual.recorder.state === "inactive") {
        finalizar();
        return;
      }

      atual.recorder.onstop = finalizar;
      atual.recorder.stop();
    });

  const handleUploadAudio = async () => {
    setLoading(true);
    try {
      const gravado = await encerrarGravacao();

      if (!gravado) {
        setRecording(false);
        setLoading(false);
        return;
      }

      // Áudio curto demais era descartado em silêncio: a pessoa gravava,
      // soltava, e simplesmente não acontecia nada — parecia microfone
      // quebrado. Agora ela sabe por que não foi.
      if (gravado.blob.size < 2000) {
        toast.info("Gravação muito curta. Segure o botão enquanto fala.");
        setLoading(false);
        setRecording(false);
        return;
      }

      // Converte para WAV antes de mandar: o formato do MediaRecorder não
      // declara a duração, e a biblioteca do WhatsApp precisa dela para
      // montar a mensagem de voz.
      const wav = await paraWav(gravado.blob);

      const formData = new FormData();
      const filename = `${new Date().getTime()}.wav`;
      formData.append("medias", wav, filename);
      // Sem legenda: mandar o nome do arquivo aqui fazia o áudio chegar no
      // celular do cliente com "1785342981.mp3" escrito embaixo, e a lista de
      // atendimentos mostrar isso como prévia da conversa.
      formData.append("body", "");
      formData.append("fromMe", true);

      await api.post(`/messages/${ticketId}`, formData);
    } catch (err) {
      toastError(err);
    }

    setRecording(false);
    setLoading(false);
  };

  const handleCancelAudio = async () => {
    await encerrarGravacao();
    setRecording(false);
  };

  const renderReplyingMessage = (message) => {
    return (
      <div className="flex w-full items-center gap-2 px-3 pt-2">
        <div
          className={cn(
            "flex flex-1 overflow-hidden rounded-lg",
            !message.fromMe ? "bg-emerald-100 dark:bg-emerald-900/30" : "bg-black/5 dark:bg-white/10"
          )}
        >
          <span
            className={cn("w-1 shrink-0", !message.fromMe ? "bg-emerald-400" : "bg-sky-400")}
          />
          <div className="min-w-0 flex-1 whitespace-pre-wrap p-2.5 text-sm">
            {!message.fromMe && (
              <span className="flex font-medium text-sky-500">
                {message.contact?.name}
              </span>
            )}
            <span className="line-clamp-2">{message.body}</span>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          disabled={loading || ticketStatus !== "open"}
          onClick={() => setReplyingMessage(null)}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  };

  const signToggle = (
    <div className="flex items-center gap-1.5">
      <Switch
        id="signMessage"
        checked={signMessage}
        onCheckedChange={(checked) => setSignMessage(checked)}
      />
      <Label htmlFor="signMessage" className="text-xs font-normal text-muted-foreground">
        {i18n.t("messagesInput.signMessage")}
      </Label>
    </div>
  );

  if (medias.length > 0) {
    return (
      <div className="flex flex-none items-center gap-2 border-t bg-muted/40 p-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => {
            setMedias([]);
            setInputMessage("");
          }}
          disabled={loading}
        >
          <X className="h-5 w-5 text-muted-foreground" />
        </Button>

        {previewMedia && (
          <img
            src={previewMedia}
            alt=""
            className="h-10 w-10 shrink-0 rounded object-cover"
          />
        )}

        <div className="min-w-0 flex-1">
          <p className="truncate text-xs text-muted-foreground">
            {medias.length > 1
              ? `${medias.length} arquivos`
              : medias[0]?.name}
          </p>
          <textarea
            ref={captionRef}
            rows={1}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onInput={adjustCaptionHeight}
            disabled={loading}
            placeholder="Escreva uma legenda (opcional)"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleUploadMedia(e);
              }
            }}
            className="min-h-[38px] max-h-[140px] w-full resize-none rounded-lg border bg-background px-3 py-1.5 text-sm placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-60 [&::-webkit-scrollbar]:hidden"
          />
        </div>

        <Button
          variant="ghost"
          size="icon"
          aria-label="Enviar arquivo"
          title="Enviar arquivo"
          onClick={handleUploadMedia}
          disabled={loading}
        >
          {loading ? (
            <Loader2 className="h-5 w-5 animate-spin text-emerald-500" />
          ) : (
            <Send className="h-5 w-5 text-muted-foreground" />
          )}
        </Button>
      </div>
    );
  }

  return (
    <div className="relative flex flex-none flex-col border-t bg-muted/40 pb-[env(safe-area-inset-bottom)]">
      <MediaLibraryPicker
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        ticketId={ticketId}
      />

      {/* Fora das barras desktop/mobile: assim o seletor funciona nos dois. */}
      {showEmoji && (
        <div
          ref={emojiBoxRef}
          className="absolute bottom-full left-2 right-2 z-20 mb-2 flex justify-center sm:right-auto sm:justify-start"
        >
          <div className="max-w-full overflow-x-auto rounded-lg shadow-lg">
            <Picker
              data={emojiData}
              locale="pt"
              perLine={8}
              previewPosition="none"
              skinTonePosition="none"
              theme={darkMode ? "dark" : "light"}
              onEmojiSelect={handleAddEmoji}
            />
          </div>
        </div>
      )}
      {replyingMessage && renderReplyingMessage(replyingMessage)}
      <div className="flex w-full items-end gap-1 p-2">
        <div className="hidden items-center gap-0.5 pb-1 sm:flex">
          <Button
            variant="ghost"
            size="icon"
            disabled={disabled}
            aria-label="Emoji"
            onClick={() => setShowEmoji((prev) => !prev)}
          >
            <Smile className="h-5 w-5 text-muted-foreground" />
          </Button>

          <input
            multiple
            type="file"
            id="upload-button"
            className="hidden"
            disabled={disabled}
            onChange={handleChangeMedias}
          />
          <label htmlFor="upload-button">
            <Button variant="ghost" size="icon" asChild disabled={disabled}>
              <span aria-label="Anexar arquivo">
                <Paperclip className="h-5 w-5 text-muted-foreground" />
              </span>
            </Button>
          </label>

          <Button
            variant="ghost"
            size="icon"
            disabled={disabled}
            aria-label="Enviar da biblioteca"
            onClick={() => setLibraryOpen(true)}
          >
            <Images className="h-5 w-5 text-muted-foreground" />
          </Button>

          <div className="ml-1 mr-2">{signToggle}</div>
        </div>

        <div className="flex pb-1 sm:hidden">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Mais opções">
                <MoreVertical className="h-5 w-5 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  setShowEmoji((prev) => !prev);
                }}
                disabled={disabled}
              >
                <Smile className="h-4 w-4" /> Emoji
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <label htmlFor="upload-button" className="flex cursor-pointer items-center gap-2">
                  <Paperclip className="h-4 w-4" /> Anexar
                </label>
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  setLibraryOpen(true);
                }}
                disabled={disabled}
              >
                <Images className="h-4 w-4" /> Biblioteca
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                {signToggle}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="relative flex-1">
          <textarea
            ref={inputRef}
            rows={1}
            placeholder={
              ticketStatus !== "open"
                ? i18n.t("messagesInput.placeholderClosed")
                : placeholderCurto
                ? "Mensagem"
                : i18n.t("messagesInput.placeholderOpen")
            }
            value={inputMessage}
            onChange={handleChangeInput}
            onInput={adjustInputHeight}
            disabled={recording || ticketStatus !== "open"}
            onPaste={(e) => {
              ticketStatus === "open" && handleInputPaste(e);
            }}
            onKeyDown={(e) => {
              if (loading || recording || ticketStatus !== "open") return;
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            className="min-h-[42px] max-h-[180px] w-full resize-none rounded-2xl border bg-background px-4 py-2.5 text-sm shadow-xs scrollbar-none placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60 [&::-webkit-scrollbar]:hidden"
          />
          {typeBar && (
            <ul className="absolute bottom-full left-0 mb-1 w-full overflow-hidden rounded-lg border bg-popover shadow-md">
              {quickAnswers.map((value, index) => (
                <li key={index} className="list-none">
                  <button
                    type="button"
                    onClick={() => handleQuickAnswersClick(value)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
                  >
                    {/* Clipe avisa que vem arquivo junto: sem isso a pessoa
                        escolhe achando que é só texto e leva um anexo. */}
                    {value.mediaUrl && (
                      <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    )}
                    <span className="truncate">
                      {value.message
                        ? `${value.shortcut} - ${value.message}`
                        : `${value.shortcut} - ${value.mediaName || "arquivo"}`}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Correção e sugestão ficam colados no campo, e não num painel à
            parte: é aqui que a pessoa está escrevendo quando precisa delas. */}
        <AssistenteDeTexto
          ticketId={ticketId}
          texto={inputMessage}
          aoTrocarTexto={setInputMessage}
          desabilitado={recording || loading || ticketStatus !== "open"}
        />

        {inputMessage ? (
          <Button
            size="icon"
            className="shrink-0 rounded-full"
            aria-label="Enviar mensagem"
            onClick={handleSendMessage}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        ) : recording ? (
          <div className="flex items-center">
            <Button variant="ghost" size="icon" disabled={loading} onClick={handleCancelAudio}>
              <XCircle className="h-5 w-5 text-destructive" />
            </Button>
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin text-emerald-500" />
            ) : (
              <RecordingTimer />
            )}
            <Button variant="ghost" size="icon" onClick={handleUploadAudio} disabled={loading}>
              <CheckCircle2 className="h-5 w-5 text-emerald-500" />
            </Button>
          </div>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            disabled={loading || ticketStatus !== "open"}
            onClick={handleStartRecording}
            aria-label="Gravar áudio"
          >
            <Mic className="h-5 w-5 text-muted-foreground" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default MessageInput;
